import React, { useState, useEffect, useCallback } from "react";
import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { useLocation } from "react-router-dom";
import { useSpace } from "../../../contexts/SpaceContext";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormHelperText,
  InputAdornment,
} from "@mui/material";

import Code from "../../../components/Code";
import { copyToClipboard } from "./ClipboardHelper";

// Import the GET_ALL_WIDGETS query to update cache
const GET_ALL_WIDGETS = gql`
  query GetAllWidgets($spaceId: ID) {
    getAllWidgets(spaceId: $spaceId) {
      id
      business_details {
        name
        display_name
        business_type
        website_url
      }
      script_id
    }
  }
`;

const CREATE_WIDGET = gql`
  mutation CreateWidget($input: WidgetInput!) {
    createWidget(input: $input) {
      id
      script_id
      business_details {
        business_type
        name
        display_name
        website_url
      }
    }
  }
`;

const UPDATE_WIDGET = gql`
  mutation UpdateWidget($id: ID!, $input: WidgetInput!) {
    updateWidget(id: $id, input: $input) {
      id
      script_id
      business_details {
        business_type
        name
        display_name
        website_url
      }
    }
  }
`;

const TEST_WIDGET_INTEGRATION = gql`
  mutation TestWidgetIntegration($scriptId: String!, $websiteUrl: String!) {
    testWidgetIntegration(scriptId: $scriptId, websiteUrl: $websiteUrl) {
      success
      message
    }
  }
`;

// Helper to detect if running in local development environment
const isLocalEnvironment = () => {
  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "laptop" ||
    hostname.endsWith(".local")
  );
};

function EventEntryTab({
  widgetData,
  isEditing,
  widgetId,
  onSave,
  onIntegrationResult,
  integrationVerified,
  onWidgetCreated,
}) {
  const location = useLocation();
  const { currentSpace } = useSpace();

  // Get spaceId from URL params or use current space
  const queryParams = new URLSearchParams(location.search);
  const spaceId = queryParams.get("spaceId") || currentSpace?.id;

  const [generatedWidgetId, setGeneratedWidgetId] = useState(
    widgetData?.script_id || ""
  );
  const [createdWidgetId, setCreatedWidgetId] = useState(widgetId || null); // Store actual widget ID
  const [businessType, setBusinessType] = useState(
    widgetData?.business_details?.business_type || ""
  );
  const [name, setName] = useState(widgetData?.business_details?.name || "");
  const [displayName, setDisplayName] = useState(
    widgetData?.business_details?.display_name || ""
  );
  const [websiteUrl, setWebsiteUrl] = useState(() => {
    const url = widgetData?.business_details?.website_url || "";
    // Strip https:// or http:// prefix if present (we show it as InputAdornment)
    return url.replace(/^https?:\/\//i, "");
  });
  const [scriptTag, setScriptTag] = useState("");
  const [error, setError] = useState(null);
  const [integrationTestResult, setIntegrationTestResult] = useState(null);
  const [widgetCreated, setWidgetCreated] = useState(!!widgetId); // Track if widget is already created
  const [urlError, setUrlError] = useState("");
  const [nameError, setNameError] = useState("");
  const [displayNameError, setDisplayNameError] = useState("");
  const [businessTypeError, setBusinessTypeError] = useState("");
  const [customBaseUrl, setCustomBaseUrl] = useState("");

  // URL normalization function - auto-adds https://, removes duplicates, cleans up URL
  const normalizeUrl = (url) => {
    if (!url) return "";

    let normalized = url.trim();

    // Remove multiple protocols (e.g., "https://https://", "http://https://")
    normalized = normalized.replace(/^(https?:\/\/)+/gi, "");

    // Remove leading/trailing slashes and whitespace
    normalized = normalized.replace(/\/+$/, "").trim();

    // Add https:// prefix if not empty and doesn't start with http
    if (normalized && !normalized.toLowerCase().startsWith("http")) {
      normalized = "https://" + normalized;
    }

    return normalized;
  };

  // URL validation function - required field
  // Note: url parameter is domain only (without https://), we prepend it for validation
  const validateUrl = (url) => {
    if (!url || !url.trim()) return "Website URL is required";

    // Prepend https:// since we store domain only
    const fullUrl = "https://" + url.trim();

    // Basic URL pattern (supports domains and subdomains)
    const urlPattern =
      /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;

    // Also allow localhost for development
    const localhostPattern =
      /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/.*)?$/;

    // Allow IP addresses
    const ipPattern =
      /^https?:\/\/\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}(:\d+)?(\/.*)?$/;

    // Allow local hostnames without TLD (e.g., laptop, laptop:3443)
    const localHostnamePattern =
      /^https?:\/\/[a-zA-Z][a-zA-Z0-9-]*(:\d+)?(\/.*)?$/;

    if (
      !urlPattern.test(fullUrl) &&
      !localhostPattern.test(fullUrl) &&
      !ipPattern.test(fullUrl) &&
      !localHostnamePattern.test(fullUrl)
    ) {
      return "Please enter a valid URL (e.g., example.com or subdomain.example.com)";
    }

    return "";
  };

  // Handle URL input change with validation
  const handleWebsiteUrlChange = (e) => {
    let value = e.target.value;
    // Remove any protocol prefix if user pastes full URL
    value = value.replace(/^https?:\/\//i, "");
    setWebsiteUrl(value);
    if (urlError) setUrlError(""); // Clear error while typing
  };

  // Handle URL input blur - validate
  const handleWebsiteUrlBlur = () => {
    // Clean up trailing slashes
    if (websiteUrl) {
      const cleaned = websiteUrl.trim().replace(/\/+$/, "");
      setWebsiteUrl(cleaned);
      setUrlError(validateUrl(cleaned));
    } else {
      setUrlError(validateUrl("")); // Validate empty to show required error
    }
  };

  // Widget Name validation - required, min 2 chars, max 50 chars
  const validateName = (value) => {
    if (!value || !value.trim()) {
      return "Widget name is required";
    }
    if (value.trim().length < 2) {
      return "Widget name must be at least 2 characters";
    }
    if (value.trim().length > 50) {
      return "Widget name must be less than 50 characters";
    }
    return "";
  };

  // Display Company Name validation - required, min 2 chars, max 100 chars
  const validateDisplayName = (value) => {
    if (!value || !value.trim()) {
      return "Display company name is required";
    }
    if (value.trim().length < 2) {
      return "Display company name must be at least 2 characters";
    }
    if (value.trim().length > 100) {
      return "Display company name must be less than 100 characters";
    }
    return "";
  };

  // Widget Name handlers
  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    if (nameError) setNameError(""); // Clear error while typing
  };

  const handleNameBlur = () => {
    setNameError(validateName(name));
  };

  // Display Company Name handlers
  const handleDisplayNameChange = (e) => {
    const value = e.target.value;
    setDisplayName(value);
    if (displayNameError) setDisplayNameError(""); // Clear error while typing
  };

  const handleDisplayNameBlur = () => {
    setDisplayNameError(validateDisplayName(displayName));
  };

  // Widget Type validation - required
  const validateBusinessType = (value) => {
    if (!value) {
      return "Widget type is required";
    }
    return "";
  };

  // Widget Type change handler
  const handleBusinessTypeChange = (e) => {
    const value = e.target.value;
    setBusinessType(value);
    if (businessTypeError) setBusinessTypeError("");
  };

  const [createWidget, { loading: createLoading }] = useMutation(
    CREATE_WIDGET,
    {
      update(cache, { data }) {
        // Update the cache to include the new widget
        if (data?.createWidget && spaceId) {
          try {
            // Read the existing widgets from cache
            const existingWidgets = cache.readQuery({
              query: GET_ALL_WIDGETS,
              variables: { spaceId: spaceId },
            });

            if (existingWidgets?.getAllWidgets) {
              // Write the updated list back to cache
              cache.writeQuery({
                query: GET_ALL_WIDGETS,
                variables: { spaceId: spaceId },
                data: {
                  getAllWidgets: [
                    data.createWidget,
                    ...existingWidgets.getAllWidgets,
                  ],
                },
              });
              console.log("✅ Apollo cache updated with new widget");
            }
          } catch (error) {
            console.log(
              "⚠️ Cache update failed (cache might be empty):",
              error.message
            );
            // This is normal if the cache doesn't exist yet
          }
        }
      },
      onError: (error) => {
        console.error("Error creating widget:", error);
        setError(error.message);
      },
    }
  );

  const [updateWidget, { loading: updateLoading }] = useMutation(
    UPDATE_WIDGET,
    {
      onError: (error) => {
        console.error("Error updating widget:", error);
        setError(error.message);
      },
    }
  );

  const [testWidgetIntegration, { loading: testLoading }] = useMutation(
    TEST_WIDGET_INTEGRATION,
    {
      onError: (error) => {
        console.error("Error testing widget integration:", error);
        setIntegrationTestResult({ success: false, message: error.message });
      },
    }
  );

  // Update form fields when widget data is loaded (for editing mode)
  useEffect(() => {
    if (widgetData) {
      const scriptId = widgetData.script_id || "";
      setGeneratedWidgetId(scriptId);
      setCreatedWidgetId(widgetData.id || widgetId); // Set the widget ID for updates
      setBusinessType(widgetData.business_details?.business_type || 1);
      setName(widgetData.business_details?.name || "");
      setDisplayName(widgetData.business_details?.display_name || "");
      // Strip https:// or http:// prefix since we show it as InputAdornment
      const url = widgetData.business_details?.website_url || "";
      setWebsiteUrl(url.replace(/^https?:\/\//i, ""));

      // Generate script tag for existing widget
      if (scriptId) {
        setScriptTag(renderScriptTag(scriptId));
      }
    }
  }, [widgetData, widgetId]);

  // Regenerate script tag when custom base URL changes
  useEffect(() => {
    if (generatedWidgetId) {
      setScriptTag(renderScriptTag(generatedWidgetId));
    }
  }, [customBaseUrl, generatedWidgetId]);

  const handleSaveWidget = useCallback(async () => {
    // Validate all fields before saving
    const businessTypeErr = validateBusinessType(businessType);
    const nameErr = validateName(name);
    const displayNameErr = validateDisplayName(displayName);
    const urlErr = validateUrl(websiteUrl);

    setBusinessTypeError(businessTypeErr);
    setNameError(nameErr);
    setDisplayNameError(displayNameErr);
    setUrlError(urlErr);

    if (businessTypeErr || nameErr || displayNameErr || urlErr) {
      return; // Don't proceed if validation fails
    }

    try {
      const widgetInput = {
        business_details: {
          business_type: businessType,
          name,
          display_name: displayName,
          website_url: "https://" + websiteUrl.trim(), // Prepend https:// since we only store domain
        },
        spaceId: spaceId, // Add space association when creating/updating widget
      };

      if (isEditing || widgetCreated) {
        // Update existing widget (either in edit mode or already created)
        const currentWidgetId = widgetId || createdWidgetId;
        if (!currentWidgetId) {
          console.error("Update failed - no widget ID available:", {
            widgetId,
            createdWidgetId,
            isEditing,
            widgetCreated,
          });
          throw new Error("Widget ID is required for update");
        }

        console.log("Updating widget with ID:", currentWidgetId);
        const { data } = await updateWidget({
          variables: {
            id: currentWidgetId,
            input: widgetInput,
          },
        });
        if (data && data.updateWidget) {
          console.log("Widget updated successfully:", data.updateWidget);
          return { widgetId: currentWidgetId, widgetData: data.updateWidget };
        } else {
          throw new Error("Failed to update widget");
        }
      } else {
        // Create new widget only if not already created
        console.log("Creating new widget...");
        const { data } = await createWidget({
          variables: {
            input: widgetInput,
          },
        });
        if (data && data.createWidget) {
          setGeneratedWidgetId(data.createWidget.script_id);
          setCreatedWidgetId(data.createWidget.id); // Store the actual widget ID
          setScriptTag(renderScriptTag(data.createWidget.script_id));
          setWidgetCreated(true); // Mark as created to prevent duplicate creation

          // Notify parent of widget creation
          if (onWidgetCreated) {
            onWidgetCreated(data.createWidget.id, data.createWidget);
          }

          console.log("Widget created successfully:", data.createWidget);

          // Return the created widget data for parent to use
          return {
            widgetId: data.createWidget.id,
            widgetData: data.createWidget,
          };
        } else {
          throw new Error("Failed to create widget");
        }
      }

      setError(null);
    } catch (err) {
      // Error is handled in the onError callback of useMutation
      console.error("Error saving widget:", err);
      throw err; // Re-throw so parent can handle the error
    }
  }, [
    businessType,
    name,
    displayName,
    websiteUrl,
    spaceId,
    isEditing,
    widgetId,
    widgetCreated,
    createdWidgetId,
    generatedWidgetId,
    updateWidget,
    createWidget,
    onWidgetCreated,
  ]);

  // Expose save function to parent component
  useEffect(() => {
    if (onSave && typeof onSave === "function") {
      onSave(handleSaveWidget);
    }
  }, [onSave, handleSaveWidget]);

  const handleTestIntegration = async () => {
    try {
      const fullUrl = "https://" + websiteUrl.trim();
      console.log("!!!!!!! scriptId", generatedWidgetId, fullUrl);
      const { data } = await testWidgetIntegration({
        variables: {
          scriptId: generatedWidgetId,
          websiteUrl: fullUrl,
        },
      });
      setIntegrationTestResult(data.testWidgetIntegration);
      // Notify parent of integration result
      if (onIntegrationResult) {
        onIntegrationResult(data.testWidgetIntegration.success);
      }
    } catch (err) {
      // Error is handled in the onError callback of useMutation
    }
  };

  const renderScriptTag = (scriptId) => {
    // Use custom base URL if provided (local dev only), otherwise use current origin
    let baseUrl = window.location.origin;
    if (customBaseUrl && customBaseUrl.trim()) {
      const customUrl = customBaseUrl.trim();
      // Ensure it has protocol
      baseUrl = customUrl.startsWith("http")
        ? customUrl
        : `https://${customUrl}`;
      // Remove trailing slash if present
      baseUrl = baseUrl.replace(/\/$/, "");
    }

    return `<!-- InstaConnect Widget -->
<script id="${scriptId}" src="${baseUrl}/widget-v1.js"></script>`.trim();
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Row 1: Widget Type & Widget Name */}
        <Grid item xs={12} md={6}>
          <FormControl
            fullWidth
            variant="outlined"
            required
            error={!!businessTypeError}
          >
            <InputLabel shrink>Widget Type</InputLabel>
            <Select
              value={businessType}
              onChange={handleBusinessTypeChange}
              label="Widget Type"
              displayEmpty
            >
              <MenuItem value="" disabled>
                <em>Select widget type</em>
              </MenuItem>
              <MenuItem value={1}>Sales</MenuItem>
              <MenuItem value={2}>Support</MenuItem>
            </Select>
            {businessTypeError && (
              <FormHelperText>{businessTypeError}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Widget Name"
            value={name}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            variant="outlined"
            fullWidth
            required
            error={!!nameError}
            helperText={nameError}
          />
        </Grid>

        {/* Row 2: Display Name & Website URL */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Display Company Name"
            value={displayName}
            onChange={handleDisplayNameChange}
            onBlur={handleDisplayNameBlur}
            variant="outlined"
            fullWidth
            required
            error={!!displayNameError}
            helperText={displayNameError}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Website URL"
            value={websiteUrl}
            onChange={handleWebsiteUrlChange}
            onBlur={handleWebsiteUrlBlur}
            variant="outlined"
            fullWidth
            required
            placeholder="example.com"
            error={!!urlError}
            helperText={
              urlError ||
              (integrationVerified
                ? "Integration verified - URL cannot be changed"
                : "")
            }
            disabled={integrationVerified}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">https://</InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Custom Widget Script URL - Local Development Only */}
        {isLocalEnvironment() && (
          <Grid item xs={12} md={6}>
            <TextField
              label="Custom Widget Script URL (Local Dev)"
              value={customBaseUrl}
              onChange={(e) => setCustomBaseUrl(e.target.value)}
              variant="outlined"
              fullWidth
              placeholder="e.g., laptop:3443 or https://laptop:3443"
              helperText="Optional: Override the widget script URL for testing"
            />
          </Grid>
        )}

        {/* Create Button */}
        {!isEditing && (
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveWidget}
              disabled={createLoading}
              sx={{ mt: 1 }}
            >
              {createLoading ? <CircularProgress size={24} /> : "Create Widget"}
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Website Integration Section */}
      {generatedWidgetId && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Website Integration
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              1. Integrate the code into the footer of your website
            </Typography>
            <Code>{scriptTag}</Code>
            <Button
              variant="outlined"
              color="primary"
              sx={{ mt: 2 }}
              onClick={() => {
                copyToClipboard(
                  scriptTag,
                  () => alert("Script copied to clipboard!"),
                  function errorCallback() {
                    alert(
                      "Failed to copy script. Please try copying it manually."
                    );
                  }
                );
              }}
            >
              Copy Script
            </Button>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              2. After adding the script to your website, test the integration
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTestIntegration}
              disabled={testLoading}
            >
              {testLoading ? (
                <CircularProgress size={24} />
              ) : (
                "Test Widget Integration"
              )}
            </Button>
            {integrationTestResult && (
              <Alert
                severity={integrationTestResult.success ? "success" : "error"}
                sx={{ mt: 2 }}
              >
                {integrationTestResult.message}
              </Alert>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              3. Do not forget to complete your privacy policy on your website
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                const privacyText = `By using our chat widget, your conversations may be recorded for quality assurance and training purposes. Your personal data will be processed in accordance with our Privacy Policy.`;
                copyToClipboard(
                  privacyText,
                  () => alert("Privacy text copied to clipboard!"),
                  function errorCallback() {
                    alert(
                      "Failed to copy privacy text. Please try copying it manually."
                    );
                  }
                );
              }}
            >
              Copy Privacy Text
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function PrepareWebsite({
  widgetData,
  isEditing,
  widgetId,
  onSave,
  onIntegrationResult,
  integrationVerified,
  onWidgetCreated,
}) {
  return (
    <React.Fragment>
      <EventEntryTab
        widgetData={widgetData}
        isEditing={isEditing}
        widgetId={widgetId}
        onSave={onSave}
        onIntegrationResult={onIntegrationResult}
        integrationVerified={integrationVerified}
        onWidgetCreated={onWidgetCreated}
      />
    </React.Fragment>
  );
}

export default PrepareWebsite;
